import { useLayoutEffect, useRef, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from "react";
import { cn } from "./cn";

/**
 * Copies each column's header text onto its body cells as data-label, so
 * below the md breakpoint the CSS in styles.css (.omf-table) can lay every
 * row out as a card of "label: value" lines instead of a wide table. Runs
 * again whenever rows change, so no page has to pass labels by hand.
 */
function labelCells(table: HTMLTableElement) {
  const labels: string[] = [];
  table.querySelectorAll<HTMLTableCellElement>("thead tr:first-child > th").forEach((th) => {
    const text = (th.getAttribute("data-label") ?? th.textContent ?? "").trim();
    for (let i = 0; i < Math.max(1, th.colSpan); i++) labels.push(text);
  });
  table.querySelectorAll<HTMLTableRowElement>("tbody > tr").forEach((tr) => {
    let col = 0;
    for (const cell of Array.from(tr.cells)) {
      const span = Math.max(1, cell.colSpan);
      if (span >= labels.length && labels.length > 1) {
        cell.setAttribute("data-full", "");
      } else {
        const label = labels[col] ?? "";
        if (cell.getAttribute("data-label") !== label) cell.setAttribute("data-label", label);
        // The trailing actions column ("Thao tác", or unlabeled) becomes the card's footer.
        const actions = col === labels.length - 1 && (label === "" || label === "Thao tác");
        cell.toggleAttribute("data-actions", actions);
      }
      col += span;
    }
  });
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLTableElement>(null);
  useLayoutEffect(() => {
    const table = ref.current;
    if (!table) return;
    labelCells(table);
    // Rows/cells added or removed (not text ticking inside a cell), at most
    // once per frame however many mutations a render makes.
    let frame = 0;
    const obs = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        labelCells(table);
      });
    });
    obs.observe(table, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div className={cn("omf-table-wrap overflow-x-auto", className)}>
      <table ref={ref} className="omf-table w-full border-collapse text-left text-[13px]">
        {children}
      </table>
    </div>
  );
}

export function Th({ className, children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        "sticky top-0 z-[1] border-b border-line bg-surface px-3 py-2 text-[11px] font-semibold tracking-wide whitespace-nowrap text-ink-3 uppercase",
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({ className, children, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn("border-b border-line px-3 py-2 align-middle", className)} {...rest}>
      {children}
    </td>
  );
}

export function Tr({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <tr className={cn("transition-colors duration-150 hover:bg-surface-2/60", onClick && "cursor-pointer", className)} onClick={onClick}>
      {children}
    </tr>
  );
}
